import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { Component, HostListener, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, filter, map, of, switchMap } from 'rxjs';
import {
  LOGO_URL,
  NavLink,
  STATIC_NAV,
  TOPBAR_PHONE,
  TOPBAR_PHONE_HREF
} from '../../core/constants/basel';
import { APP_NAME } from '../../core/constants/app.constants';
import { AdminChatSessionService } from '../../core/services/admin-chat-session.service';
import { AuthService } from '../../core/services/auth.service';
import { CartService } from '../../core/services/cart.service';
import { CatalogService } from '../../core/services/catalog.service';
import { OverlayService } from '../../core/services/overlay.service';
import { formatMoney } from '../../core/utils/money.util';
import { AdminChatNotifierComponent } from '../../shared/components/admin-chat-notifier/admin-chat-notifier.component';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { TranslationKey } from '../../core/i18n/translations/en';
import { CatalogProduct } from '../../core/models/catalog.model';
import { UserRole } from '../../core/models/auth.model';

@Component({
  selector: 'app-header',
  imports: [RouterLink, DecimalPipe, NgTemplateOutlet, AdminChatNotifierComponent, TranslatePipe],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss'
})
export class HeaderComponent {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly chatSession = inject(AdminChatSessionService);
  private readonly searchInput$ = new Subject<string>();
  readonly overlay = inject(OverlayService);
  readonly cart = inject(CartService);
  readonly catalog = inject(CatalogService);

  readonly isAdminLoggedIn = this.auth.isLoggedIn;
  readonly user = this.auth.user;

  readonly logo = LOGO_URL;
  readonly displayName = APP_NAME;
  readonly nav = computed(() => {
    const categories = this.catalog.categoryNav();
    const home = STATIC_NAV[0];
    const rest = STATIC_NAV.slice(1);
    return [home, ...categories, ...rest];
  });
  readonly topbarPhone = TOPBAR_PHONE;
  readonly topbarPhoneHref = TOPBAR_PHONE_HREF;
  readonly scrolled = signal(false);
  readonly searchQuery = signal('');
  readonly searchResults = signal<CatalogProduct[]>([]);
  readonly url = signal(this.router.url);

  readonly cartSubtotal = computed(() => formatMoney(this.cart.subtotal()));

  constructor() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      this.url.set((event as NavigationEnd).urlAfterRedirects);
      this.overlay.closeAll();
    });

    this.searchInput$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((raw) => {
          const query = raw.trim();
          if (!query) {
            return of([] as CatalogProduct[]);
          }
          return this.catalog
            .queryProducts({ search: query, pageSize: 8 })
            .pipe(map((page) => page.items));
        }),
        takeUntilDestroyed()
      )
      .subscribe((items) => this.searchResults.set(items));
  }

  @HostListener('window:scroll')
  onScroll(): void {
    this.scrolled.set(window.scrollY > 80);
  }

  isCurrent(link: NavLink): boolean {
    const [path, queryString] = this.url().split('?');
    if (link.path === '/') {
      return path === '/';
    }
    if (path !== link.path && !path.startsWith(`${link.path}/`)) {
      return false;
    }
    const params = new URLSearchParams(queryString || '');
    if (!link.query) {
      // Category menus use `/shop/:slug` (HVAC-style); plain `/shop` is "all products".
      if (link.path === '/shop') {
        return path === '/shop' && !params.get('brand') && !params.get('q') && !params.get('kind');
      }
      return true;
    }
    return Object.entries(link.query).every(([key, value]) => params.get(key) === value);
  }

  openSearch(event: Event): void {
    event.preventDefault();
    this.overlay.openSearch();
  }

  openCart(event: Event): void {
    event.preventDefault();
    this.overlay.openCart();
  }

  openMenu(event: Event): void {
    event.preventDefault();
    this.overlay.openMenu();
  }

  closeMenu(): void {
    this.overlay.closeMenu();
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchQuery.set(value);
    this.searchInput$.next(value);
  }

  submitSearch(event: Event): void {
    event.preventDefault();
    const query = this.searchQuery().trim();
    this.overlay.closeSearch();
    void this.router.navigate(['/shop'], { queryParams: query ? { q: query } : {} });
  }

  logout(): void {
    void this.chatSession.stop();
    this.auth.logout();
    void this.router.navigateByUrl('/login');
  }

  roleKey(role: UserRole): TranslationKey {
    return role === 'BusinessAdmin' ? 'users.role.BusinessAdmin' : 'users.role.PortalAdmin';
  }
}
